<template>
  <div class="mp-selector">
    <div class="mp-list">
      <div class="mp-search">
        <text-field
          v-model="filter"
          :placeholder="$mpt($t('field.sensor_filter'))"
          prepend-inner-icon="mdi-magnify"
          hide-details
          dense
          data-testid="sensor_filter"
        />
      </div>
      <div v-for="group in groups" :key="group.type" class="mp-group">
        <div class="mp-group__title" data-testid="sensor_group_title">{{ group.label }}</div>
        <v-checkbox
          v-for="sensor in group.sensors"
          :key="sensor.id"
          :value="sensor.id"
          v-model="selectedIds"
          hide-details
          dense
          data-testid="sensor_checkbox"
        >
          <template #label>
            <span class="mp-item" data-testid="monitoring_point_item">
              <span class="mp-item__head">
                <span
                  class="mp-item__name text-truncate min-w-0"
                  :title="sensor.name"
                  data-testid="sensor_name"
                >{{ sensor.name }}</span>
                <status-chip v-if="sensor.status" :status="sensor.status" class="ml-auto flex-shrink-0" />
              </span>
              <span
                v-if="sensor.place"
                class="mp-item__place text-truncate min-w-0"
                :title="sensor.place"
                data-testid="sensor_place"
              >{{ sensor.place }}</span>
            </span>
          </template>
        </v-checkbox>
      </div>

      <div v-if="isEmpty" class="mp-empty" data-testid="no_applicable_sensors_message">
        {{ $mpt($t('msg.no_applicable_sensors')) }}
      </div>
    </div>
  </div>
</template>

<i18n lang="yaml">
ja:
  field:
    sensor_filter: センサー名で絞り込み
  group:
    unknown: 不明
  msg:
    unknown_sensor: 不明なセンサー
    no_applicable_sensors: 該当するセンサーがありません
en:
  field:
    sensor_filter: Filter by sensor name
  group:
    unknown: Unknown
  msg:
    unknown_sensor: Unknown sensor
    no_applicable_sensors: No matching sensors
</i18n>

<script>
import TextField from '@/components/atoms/TextField';
import StatusChip from '@/components/atoms/StatusChip';
import SensorTypes from '@/mixins/sensorTypes';
import { buildSensorSections } from '@/libs/sensorSections';

export default {
  name: 'monitoring-point-selector',
  components: { TextField, StatusChip },
  mixins: [SensorTypes],
  props: {
    value: { type: Array, default: () => [] },
    sensors: { type: Array, default: () => [] },
    orphanedSensorIds: { type: Array, default: () => [] },
    visible: { type: Boolean, default: false },
  },
  data() {
    return { filter: '' };
  },
  computed: {
    selectedIds: {
      get() {
        return this.value;
      },
      set(ids) {
        this.$emit('input', ids);
      },
    },
    filteredSensors() {
      if (!this.filter) return this.sensors;
      const query = this.filter.toLowerCase();
      return this.sensors.filter(s => s.name.toLowerCase().includes(query));
    },
    // 一覧に無いセンサーは名前も種別も引けないため、絞り込みの対象外にして
    // 「不明」グループへ束ねる。種別グループと同じ形にすることで、
    // テンプレート側は groups を 1 本の v-for で回すだけで済む。
    unknownGroup() {
      if (this.orphanedSensorIds.length === 0) return null;
      return {
        type: 'unknown',
        label: this.$t('group.unknown'),
        sensors: this.orphanedSensorIds.map(id => ({
          id,
          name: this.$mpt(this.$t('msg.unknown_sensor')),
        })),
      };
    },
    groups() {
      const sections = buildSensorSections(this.filteredSensors, {
        typeNames: this.SENSOR_TYPE_NAMES,
        inactiveLast: true,
      });
      return this.unknownGroup ? [...sections, this.unknownGroup] : sections;
    },
    isEmpty() {
      return this.groups.length === 0;
    },
  },
  watch: {
    // 親の resetForm() は子の data に届かないため、開いた時点で自分で消す
    visible(val) {
      if (val) {
        this.filter = '';
      }
    },
  },
};
</script>

<style scoped>
.mp-selector {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* 内側の左右余白は親が決める。親が既に余白を持つ場合は 0 のままでよい */
.mp-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 var(--mp-list-inset, 0px) 4px;
}

/* 絞り込み欄は一覧と同じスクロール領域に置き、上端へ貼り付ける。
   行がこの下を通るため、背景は面と同色の不透明色が要る。
   背景を左右いっぱいに広げるため、一覧の内側余白を打ち消して自分で持つ */
.mp-search {
  position: sticky;
  top: 0;
  z-index: 3;
  margin-left: calc(-1 * var(--mp-list-inset, 0px));
  margin-right: calc(-1 * var(--mp-list-inset, 0px));
  padding: 8px var(--mp-list-inset, 0px) 6px;
  background: var(--mp-surface, #f5f5f5);
}

/* 固定時に下の行を隠すため、背景を親のサイドバーの面と同色にする。
   色は親が --mp-surface で供給する（CSS 変数はコンポーネント境界を越えて継承される）。
   top は .mp-search の高さに合わせる */
.mp-group__title {
  position: sticky;
  top: 64px;
  z-index: 2;
  background: var(--mp-surface, #f5f5f5);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  padding: 10px 2px 7px;
  border-bottom: 1px solid #e0e0e0;
}

.mp-group + .mp-group {
  margin-top: 16px;
}

.mp-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.mp-item__head {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 24px; /* チェックボックスのアイコン高と揃え、名前を同じ中心線に乗せる */
}

.mp-item__name {
  font-size: 14px;
}

.mp-item__place {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.38);
}

.mp-empty {
  color: rgba(0, 0, 0, 0.38);
  font-size: 13px;
  padding: 14px 2px;
}

::v-deep .v-input {
  font-size: 14px;
  height: 50px;
}

::v-deep .v-label {
  font-size: 14px;
  min-width: 0;
}

/* 行が名前 + 設置場所の 2 段になるため、チェックボックスを縦中央ではなく名前の横に置く */
::v-deep .v-input--selection-controls .v-input__slot {
  align-items: flex-start;
}
</style>
